import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { breakpoints, media } from 'src/assets/_responsive-mixin';
import {
  Dropdown,
  DropdownItem
} from 'src/components/shared/Dropdown/Dropdown';
import { Icon } from 'src/components/shared/Icon/Icon';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import styled from 'styled-components';
import { ProfileForm } from '../Users/ProfileForm';
import logo from 'src/assets/images/logos/washingtonmills_logo.png';
import { Drawer, useMediaQuery } from '@mui/material';
import { useAuth } from '../Auth/AuthContext';

type HeaderProps = {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
};

export const Header = ({ isSidebarOpen, onToggleSidebar }: HeaderProps) => {
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const [profileOpen, setProfileOpen] = useState<boolean>(false);

  const { t } = useTranslation();
  const { user, signout } = useAuth();

  const smallScreen = useMediaQuery(`(max-width: ${breakpoints.small})`);

  const onSignout = () => {
    signout();
  };

  return (
    <>
      <HeaderContainer>
        {smallScreen && (
          <NavButton className="mr-60" onClick={onToggleSidebar}>
            <Hamburger className={isSidebarOpen ? 'open' : ''}>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </Hamburger>
          </NavButton>
        )}

        <Logo src={logo} alt="LIMS logo" />

        <RightContainer>
          <NavItem>
            <BodyType className="mr-8">{user?.username}</BodyType>
            <NavButton onClick={() => setUserMenuOpen(!userMenuOpen)}>
              <Icon type="la-user" />
            </NavButton>
            <Dropdown
              active={userMenuOpen}
              onClose={() => setUserMenuOpen(false)}
            >
              <DropdownItem onClick={() => setProfileOpen(!profileOpen)}>
                <Icon type="la-user" /> {t('profile')}
              </DropdownItem>
              <DropdownItem onClick={onSignout}>
                <Icon type="la-sign-out-alt" /> {t('signout')}
              </DropdownItem>
            </Dropdown>
          </NavItem>
        </RightContainer>
      </HeaderContainer>

      <Drawer
        anchor="right"
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
      >
        <ProfileForm user={user} onCancel={() => setProfileOpen(false)} />
      </Drawer>
    </>
  );
};

const HeaderContainer = styled.header`
  position: fixed;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  left: 0;
  top: 0;
  width: 100%;
  height: 60px;
  background: var(--header-background);
  box-shadow: 0 1px 4px 0px rgba(0, 0, 0, 0.16);
  z-index: 100;
  padding: 0 32px 10px 48px;

  ${media.medium`
    padding: 0 32px 10px 48px;
  `}

  ${media.small`
    padding: 0 16px 10px;
  `}
`;

const RightContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-left: auto;
  height: 45px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--primary-color);
  background: transparent;
  box-shadow: 0px 2px 2px #aaa;
  border: 1px solid var(--primary-color40);
  border-radius: 50%;
  outline: none;
  height: 40px;
  width: 40px;
  transition-duration: 500ms;

  i {
    font-size: 1.5rem;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    box-shadow: 0px 2px 4px #aaa;
    transform: scale(1.02);
    color: #fff;
    background: var(--primary-color);
  }
`;

const Hamburger = styled.div<{ open?: boolean }>`
  position: relative;
  height: 14px;
  width: 20px;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: currentColor;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: transform 0.25s ease-in-out, top 0.25s ease-in-out,
      width 0.25s ease-in-out, left 0.25s ease-in-out;
  }

  span:nth-child(1) {
    top: 0px;
  }

  span:nth-child(2),
  span:nth-child(3) {
    top: 6px;
  }

  span:nth-child(4) {
    top: 12px;
  }

  &.open {
    span:nth-child(1) {
      top: 6px;
      width: 0%;
      left: 50%;
    }

    span:nth-child(2) {
      transform: rotate(45deg);
      width: 100%;
      margin-left: 0;
    }

    span:nth-child(3) {
      transform: rotate(-45deg);
      width: 100%;
      margin-left: 0;
    }

    span:nth-child(4) {
      top: 6px;
      width: 0%;
      left: 50%;
    }
  }
`;

const Logo = styled.img`
  max-width: 160px;
  height: 30px;

  ${media.xsmall`
    display: none;
  `}
`;
